import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";

import { myAxios, MyDate, weeknum } from "src/components/common/functions";

export type ImportedBillingProject = {
    id: string;
    projectNumber: string;
    projectName: string;
    projectManagerId: string;
    projectManagerName: string;
    pipeline: string;
    subPipeline: string;
};

export type ImportedBillingItem = {
    date: string;
    billingNumber: string;
    amount: number;
};

export type ImportedBillingWeekMap = Record<string, Record<string, ImportedBillingItem[]>>;

export type ImportedBillingLoaderData = {
    projects: ImportedBillingProject[];
    rawData: ImportedBillingWeekMap;
    fileExist: boolean;
    startWeek: string;
    endWeek: string;
    importWeek: string;
    projectManagerId: string;
    pipeline: string;
    subPipeline: string;
};

const allowedParams = new Set([
    "page",
    "limit",
    "startWeek",
    "endWeek",
    "importWeek",
    "projectManagerId",
    "pipeline",
    "subPipeline",
]);

function currentWeek(): string {
    return weeknum(new MyDate());
}

export async function importedBillingLoader({ request }: LoaderFunctionArgs): Promise<ImportedBillingLoaderData> {
    const url = new URL(request.url);
    let shouldRedirect = false;
    for (const [key] of url.searchParams) {
        if (!allowedParams.has(key)) {
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }

    if (!url.searchParams.get("page")) {
        url.searchParams.set("page", "1");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("limit")) {
        url.searchParams.set("limit", "10");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("startWeek")) {
        url.searchParams.set("startWeek", currentWeek());
        shouldRedirect = true;
    }
    if (!url.searchParams.get("endWeek")) {
        url.searchParams.set("endWeek", currentWeek());
        shouldRedirect = true;
    }
    if (!url.searchParams.get("importWeek")) {
        url.searchParams.set("importWeek", currentWeek());
        shouldRedirect = true;
    }
    if (!url.searchParams.get("projectManagerId")) {
        url.searchParams.set("projectManagerId", "All");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("pipeline")) {
        url.searchParams.set("pipeline", "All");
        shouldRedirect = true;
    }
    if (!url.searchParams.get("subPipeline")) {
        url.searchParams.set("subPipeline", "All");
        shouldRedirect = true;
    }
    if (shouldRedirect) {
        throw redirect(`${url.pathname}?${url.searchParams.toString()}`);
    }

    const startWeek = String(url.searchParams.get("startWeek") ?? currentWeek());
    const endWeek = String(url.searchParams.get("endWeek") ?? currentWeek());
    const importWeek = String(url.searchParams.get("importWeek") ?? currentWeek());
    const projectManagerId = String(url.searchParams.get("projectManagerId") ?? "All");
    const pipeline = String(url.searchParams.get("pipeline") ?? "All");
    const subPipeline = String(url.searchParams.get("subPipeline") ?? "All");

    const projectsRes = await myAxios({ fileName: "importedBillings/readProjects.php" });
    if (!projectsRes) throw redirect("..");

    const billingsFormData = new FormData();
    billingsFormData.append("startWeek", startWeek);
    billingsFormData.append("endWeek", endWeek);
    const billingsRes = await myAxios({
        fileName: "importedBillings/readBillings.php",
        formData: billingsFormData,
    });
    if (!billingsRes) throw redirect("..");

    const importWeekFormData = new FormData();
    importWeekFormData.append("importWeek", importWeek);
    const importWeekRes = await myAxios({
        fileName: "importedBillings/importWeek.php",
        formData: importWeekFormData,
    });
    if (!importWeekRes) throw redirect("..");

    return {
        projects: Array.isArray(projectsRes.data) ? (projectsRes.data as ImportedBillingProject[]) : [],
        rawData: (billingsRes.data as ImportedBillingWeekMap) ?? {},
        fileExist: importWeekRes.data === "yes",
        startWeek,
        endWeek,
        importWeek,
        projectManagerId,
        pipeline,
        subPipeline,
    };
}

export async function importedBillingAction({ request }: ActionFunctionArgs) {
    const url = new URL(request.url);
    const formData = await request.formData();
    const intent = String(formData.get("intent") ?? "");

    if (intent === "import") {
        if (!formData.get("week") || !formData.get("data")) {
            window.alert("Missing import data.");
            return null;
        }
        const res = await myAxios({
            fileName: "importedBillings/createBilling.php",
            formData,
        });
        if (!res) return null;
    } else if (intent === "delete") {
        if (!formData.get("week")) {
            window.alert("Missing import week.");
            return null;
        }
        const res = await myAxios({
            fileName: "importedBillings/deleteBilling.php",
            formData,
        });
        if (!res) return null;
    } else {
        window.alert("Invalid imported billing action.");
        return null;
    }

    throw redirect(`${url.pathname}?${url.searchParams.toString()}`);
}
